/**
 * Login screen.
 *
 * Collects tenant code + username + password, and an OPTIONAL branch. The
 * branch picker is a free-text branch code rather than a dropdown on purpose:
 * before authenticating there is no endpoint that can list a tenant's branches,
 * and inventing one would leak the chain's structure to anyone who guesses a
 * tenant code. Leave it empty and the API picks the user's default; the branch
 * switcher in the header handles the rest afterwards.
 *
 * Turnstile: when `TURNSTILE_SECRET_KEY` is configured the API rejects a login
 * without a token. The widget is only rendered when a site key is present in
 * the build, and the field is otherwise omitted entirely.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, FormField, Input } from '@sonauto/ui';
import { errorCode, errorMessage } from '../lib/api.js';
import { rememberedTenantCode } from '../lib/tokenStorage.js';
import { useAuth } from './AuthProvider.js';

const schema = z.object({
  tenantCode: z.string().trim().min(1, 'Vui lòng nhập mã tổ chức.').max(64),
  username: z.string().trim().min(1, 'Vui lòng nhập tên đăng nhập.').max(120),
  password: z.string().min(1, 'Vui lòng nhập mật khẩu.').max(256),
  branchId: z.string().trim().max(32).optional(),
});

type FormValues = z.infer<typeof schema>;

/** Injected at build time; empty means Turnstile is not enabled for this build. */
const TURNSTILE_SITE_KEY = (import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined) ?? '';

export function LoginPage(): React.JSX.Element {
  const { login } = useAuth();
  const [failure, setFailure] = useState<{ message: string; code: string | null } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { tenantCode: rememberedTenantCode(), username: '', password: '', branchId: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      await login({
        tenantCode: values.tenantCode,
        username: values.username,
        password: values.password,
        branchId: values.branchId === '' ? null : (values.branchId ?? null),
      });
    } catch (error) {
      setFailure({ message: errorMessage(error), code: errorCode(error) });
    }
  });

  return (
    <main className="flex min-h-full items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-brand-600 text-2xl text-white">
            🍜
          </div>
          <h1 className="text-2xl font-semibold text-fg">SonAuto Admin</h1>
          <p className="mt-1 text-sm text-fg-muted">Hệ thống quản trị chuỗi nhà hàng</p>
        </div>

        <Card>
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <FormField label="Mã tổ chức" required error={errors.tenantCode?.message}>
              {(field) => (
                <Input
                  {...field}
                  {...register('tenantCode')}
                  autoComplete="organization"
                  autoCapitalize="characters"
                  placeholder="VD: SONAUTO"
                />
              )}
            </FormField>

            <FormField label="Tên đăng nhập" required error={errors.username?.message}>
              {(field) => <Input {...field} {...register('username')} autoComplete="username" />}
            </FormField>

            <FormField label="Mật khẩu" required error={errors.password?.message}>
              {(field) => (
                <Input {...field} {...register('password')} type="password" autoComplete="current-password" />
              )}
            </FormField>

            <FormField
              label="Mã chi nhánh"
              hint="Bỏ trống để dùng chi nhánh mặc định của tài khoản. Có thể đổi sau khi đăng nhập."
              error={errors.branchId?.message}
            >
              {(field) => <Input {...field} {...register('branchId')} placeholder="Tùy chọn" />}
            </FormField>

            {TURNSTILE_SITE_KEY !== '' ? (
              <div
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITE_KEY}
                aria-label="Xác minh chống bot"
                // The Turnstile script is loaded by the host page when the key
                // is configured; the widget writes its token into the form.
              />
            ) : null}

            {failure !== null ? (
              <div role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
                <p className="font-medium">{failure.message}</p>
                {failure.code !== null ? <p className="mt-0.5 font-mono text-xs opacity-80">{failure.code}</p> : null}
              </div>
            ) : null}

            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              Đăng nhập
            </Button>
          </form>
        </Card>

        <p className="mt-6 text-center text-xs text-fg-subtle">
          Mọi thao tác trên hệ thống đều được ghi vào nhật ký kiểm toán.
        </p>
      </div>
    </main>
  );
}

export default LoginPage;
